





























































































































import Vue from 'vue';
import gql from 'graphql-tag';
import * as _ from 'lodash';

import ProfileIcon from '@/components/partials/loggedInHeader/subcomponents/ProfileIcon.vue';
import UserBadge from '@/components/partials/loggedInHeader/subcomponents/UserBadge.vue';
import TermsOfServiceModal from '@/components/partials/legalDocs/TermsOfServiceModal.vue';
import PrivacyNoticeModal from '@/components/partials/legalDocs/PrivacyNoticeModal.vue';
import VersionInfo from "@/components/partials/versionInfo/VersionInfo.vue";

import BuildingViewVariant from '@/enums/BuildingViewVariant';
import { eventBus } from '@/main';
import { OrganizationShort } from '@/interfaces/organizations/OrganizationShort';
import {
  buildingQuery,
  meRefetchableQuery
} from '@/components/pages/organization/helpers/organizationHelpers';
import { customAppLogoPathQuery } from '@/queries/customAppLogoPathQuery';
import { LegalDocStatus } from '@/enums/user/LegalDocStatus';

const mePollInterval = (process.env.WIZ_ENV != 'test' && process.env.WIZ_ENV != 'local-test')
  ? 30000
  : undefined;

const routerPagesWithoutCurrentBuildingName = [
  'Buildings',
  'BuildingList',
  'CreateABuilding',
  'FillOrganizationDetails',
  'Profile',
];

const routerPagesWithOrganizationName = [
  'Buildings',
  'BuildingList',
  'CreateABuilding',
  'Profile',
];

const routerPagesWithoutFloorSelectDropdown = [
  'BuildingList',
  'CreateABuilding',
  'Profile',
];

export default Vue.extend({
  name: 'LoggedInHeader',
  components: {
    ProfileIcon,
    UserBadge,
    TermsOfServiceModal,
    PrivacyNoticeModal,
    VersionInfo,
  },
  created() {
    eventBus.$on(
      'organization-changed',
      () => this.organizationChanged(),
    );
  },
  data(): {
    building: { name: string; };
    me: {
      email: string;
      availableTokens: number;
      organizations: any[];
      termsStatus: LegalDocStatus | undefined;
      privacyNoticeStatus: LegalDocStatus | undefined;
      dataProcessingScheduleStatus: LegalDocStatus | undefined;
    };
    customAppLogoPath: string | undefined;
    customAppLogoInitiallyLoaded: boolean;
    floors: any[];
  } {
    return {
      building: { name: '' },
      me: {
        email: '',
        availableTokens: 0,
        organizations: [],
        termsStatus: undefined,
        privacyNoticeStatus: undefined,
        dataProcessingScheduleStatus: undefined,
      },
      customAppLogoPath: undefined,
      customAppLogoInitiallyLoaded: false,
      floors: [],
    };
  },
  apollo: {
    building: {
      query() {
        return gql`
          query building($id: String!) {
            building(id: $id) {
              id
              name
            }
          }
        `;
      },
      variables(): {
        id: string;
      } {
        return { id: this.$store.state.user.currentBuildingId };
      },
      skip(): boolean{
        return (
          this.$store.state.user.currentBuildingId == undefined ||
          this.$store.state.user.currentUser.id == undefined ||
          this.$store.state.user.currentUser.organizationId == undefined
        );
      },
    },
    me: {
      query: meRefetchableQuery,
      pollInterval: mePollInterval,
      skip(): boolean {
        return (
          this.$store.state.user.currentUser.id == undefined
        );
      },
      update(dt): { email: string; availableTokens: number; organizations: any[]; } {
        if (_.isEmpty(dt)) {
          return this.me;
        }
        if(dt.me.locale) {
          localStorage.setItem("locale", dt.me.locale.toLowerCase());
          Vue.i18n.set(dt.me.locale.toLowerCase());
        } else {
          Vue.i18n.set('en');
        }
        return dt.me;
      },
    },
    customAppLogoPath: {
      query: customAppLogoPathQuery,
      variables(): {
        organizationId: string;
      } {
        return {
          organizationId: this.$store.state.user.currentUser.organizationId,
        };
      },
      result(result: any) {
        this.customAppLogoInitiallyLoaded = true;
      },
      fetchPolicy: 'no-cache',
    },
    floors: {
      query: buildingQuery,
      variables(): {
        buildingId: string;
      } {
        return { buildingId: this.$store.state.user.currentBuildingId };
      },
      update(dt) {
        const floors = _.get(dt, 'building.floors', undefined);
        return _.defaultTo(floors, []);
      },
      skip(): boolean {
        return this.$store.state.user.currentBuildingId == undefined;
      },
    },
  },
  computed: {
    landingPageHost(): string {
      let url = process.env.LANDING_PAGE_URL || '';
      const currentLocale = this.$i18n.locale();
      // NB! Landing page vue router is using history mode, no need to add `/#/`
      if (currentLocale) url += `?locale=${currentLocale}`
      return url;
    },
    appLogoPath(): string | undefined {
      if (!this.customAppLogoInitiallyLoaded) {
        return undefined;
      }
      if (
        this.$store.state.user.currentUser.organizationId
        && this.customAppLogoPath != undefined
      ) {
        return this.customAppLogoPath;
      }
      return require('@/assets/pro-logo-white.svg');
    },
    showBuildingName(): boolean {
      return (
        this.building != undefined
        && this.building.name != undefined
        && !_.includes(
          routerPagesWithoutCurrentBuildingName,
          this.$route.name,
        )
      )
    },
    showOrganizationName(): boolean {
      return (
        !this.showBuildingName != undefined
        && this.selectedOrganizationName != ''
        && _.includes(
          routerPagesWithOrganizationName,
          this.$route.name,
        )
      )
    },
    buildingName(): string {
      return (this.building && this.building.name) || '';
    },
    selectedOrganizationName(): string {
      const selectedOrganization = _.find(
        this.organizations,
        (organization: OrganizationShort) => this.isCurrentOrganization(organization.id),
      );
      if (selectedOrganization == undefined) {
        return '';
      }
      return selectedOrganization.title;
    },
    buildingViewVariants(): BuildingViewVariant[] {
      return [BuildingViewVariant.ListView, BuildingViewVariant.MapView];
    },
    viewVariantDropdownIsVisible(): boolean {
      const currentlyOnBuildingPage = (this.$route.name === 'Building')
        || (this.$route.name === 'Home');
      return (
        currentlyOnBuildingPage
      );
    },
    currentBuildingViewVariantLabel(): string {
      const variant = this.$store.state.user.currentSelectedBuildingViewVariant;
      const key = BuildingViewVariant.getTextLabelKey(variant);
      return Vue.i18n.translate(key) || '';
    },
    organizations(): OrganizationShort[] {
      if (this.me.organizations == undefined) {
        return [];
      }
      return this.me.organizations;
    },
    termsOfServiceOpened(): boolean {
      if (this.me.termsStatus == undefined) {
        return false;
      }
      return this.me.termsStatus === LegalDocStatus.NOT_ACCEPTED
        || this.me.termsStatus === LegalDocStatus.UPDATE_NOT_ACCEPTED;
    },
    termsOfServiceStatus(): LegalDocStatus | undefined {
      return this.me.termsStatus;
    },
    privacyNoticeOpened(): boolean {
      if (this.termsOfServiceOpened) {
        return false;
      }
      return this.me.privacyNoticeStatus === LegalDocStatus.NOT_ACCEPTED
        || this.me.privacyNoticeStatus === LegalDocStatus.UPDATE_NOT_ACCEPTED
        || this.me.dataProcessingScheduleStatus === LegalDocStatus.NOT_ACCEPTED
        || this.me.dataProcessingScheduleStatus === LegalDocStatus.UPDATE_NOT_ACCEPTED;
    },
    privacyNoticeStatus(): LegalDocStatus | undefined {
      if (
        this.me.privacyNoticeStatus != undefined
        && this.me.dataProcessingScheduleStatus != undefined
        && [this.me.privacyNoticeStatus, this.me.dataProcessingScheduleStatus].includes(LegalDocStatus.UPDATE_NOT_ACCEPTED)
        && ![this.me.privacyNoticeStatus, this.me.dataProcessingScheduleStatus].includes(LegalDocStatus.NOT_ACCEPTED)
      ) {
        return LegalDocStatus.UPDATE_NOT_ACCEPTED;
      }
      return LegalDocStatus.NOT_ACCEPTED;
    },
    floorSelectDropdownIsVisible(): boolean {
      return (
        !!this.$route.name
        && !_.includes(
          routerPagesWithoutFloorSelectDropdown,
          this.$route.name,
        )
      );
    },
    selectedFloorName(): string {
      if (this.floors.length) {
        let currentFloor = _.find(this.floors, (floor) => floor.id == this.$store.state.user.currentFloorId);
        return currentFloor ? currentFloor.name : null;
      } else {
        return Vue.i18n.translate("SelectFloor")!;
      }
    },
  },
  methods: {
    goToBuildingsList() {
      window.location.href = '#/buildings/building-list';
    },
    isCurrentOrganization(organizationId: string): boolean {
      return (organizationId == this.$store.state.user.currentUser.organizationId);
    },
    getBuildingViewVariantLabel(variant: BuildingViewVariant) {
      return Vue.i18n.translate(BuildingViewVariant.getTextLabelKey(variant));
    },
    selectBuildingViewVariant(variant: BuildingViewVariant) {
      this.$store.dispatch('changeCurrentBuildingViewVariant', { variant });
    },
    async signout() {
      const apolloClient = this.$apollo.provider.defaultClient;
      await this.$store.dispatch('signOut');
      await this.$store.dispatch('dropCurrentBuilding');
      await apolloClient.stop();
      await apolloClient.clearStore();
      this.$router.push('/');
    },
    async selectOrganization(organizationId: string) {
      if (this.isCurrentOrganization(organizationId)) {
        return;
      }
      const apolloClient = this.$apollo.provider.defaultClient;
      await this.$store.dispatch('selectOrganization', { organizationId, });
      await apolloClient.stop();
      await apolloClient.clearStore();
      this.$apollo.queries.me.refresh();
      if (this.$route.name != 'BuildingList') {
        this.$router.push('BuildingList');
      }
      eventBus.$emit('organization-changed');
    },
    createNewOrganization() {
      this.$router.push('/organization-details');
    },
    organizationChanged() {
      if (this.$apollo.queries.customAppLogoPath != undefined) {
        this.$apollo.queries.customAppLogoPath.refresh();
      }
      if (
        this.$store.state.user.currentUser.id != undefined
        && this.$apollo.queries.me != undefined
        && this.$apollo.queries.building != undefined
      ) {
        // That is needed for the case when component was already shown
        // and has no reasons to reload itself
        this.$apollo.queries.me.refresh();
        this.$apollo.queries.building.refresh();
      }
    },
    editProfile() {
      if (this.$route.path !== '/profile') {
        this.$router.push('/profile');
      }
    },
    selectFloor(floorId: string) {
      this.$store.dispatch('changeCurrentFloor', {
        newBuildingId: this.$store.state.user.currentBuildingId,
        newFloorId: floorId,
      });
    },
    floorName(floor: any): string {
      return floor.name;
    },
  },
});
